import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

// Build a stable cart-line key from product_id + optional variant_id.
const lineKey = (productId, variantId) =>
  variantId ? `${productId}::${variantId}` : productId;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem("cart");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  /**
   * Add a product (optionally with a variant) to the cart.
   * @param {object} product - the product doc (or partial with id/name/image/price)
   * @param {number} qty
   * @param {object} variant - optional { id, label, price_delta, image }
   */
  const add = (product, qty = 1, variant = null) => {
    const variantId = variant?.id || null;
    const variantLabel = variant?.label || null;
    const finalImage = variant?.image || product.image;
    const finalPrice = +(product.price + (variant?.price_delta || 0)).toFixed(2);
    const finalName = variantLabel ? `${product.name} — ${variantLabel}` : product.name;
    const key = lineKey(product.id, variantId);

    setItems((prev) => {
      const existing = prev.find(
        (i) => lineKey(i.product_id, i.variant_id) === key
      );
      if (existing) {
        return prev.map((i) =>
          lineKey(i.product_id, i.variant_id) === key
            ? { ...i, quantity: i.quantity + qty }
            : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          variant_id: variantId,
          variant_label: variantLabel,
          name: finalName,
          price: finalPrice,
          image: finalImage,
          quantity: qty,
        },
      ];
    });

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cart:added", {
          detail: {
            product_id: product.id,
            variant_id: variantId,
            variant_label: variantLabel,
            name: finalName,
            price: finalPrice,
            image: finalImage,
            quantity: qty,
          },
        })
      );
    }
  };

  const updateQty = (productId, qty, variantId = null) => {
    const key = lineKey(productId, variantId);
    if (qty < 1) return remove(productId, variantId);
    setItems((prev) =>
      prev.map((i) =>
        lineKey(i.product_id, i.variant_id) === key
          ? { ...i, quantity: qty }
          : i
      )
    );
  };

  const remove = (productId, variantId = null) => {
    const key = lineKey(productId, variantId);
    setItems((prev) =>
      prev.filter((i) => lineKey(i.product_id, i.variant_id) !== key)
    );
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, add, updateQty, remove, clear, subtotal, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
